import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Tablet,
  RefreshCcw,
  Users,
  ArrowLeftRight,
  SquarePlus,
  KeyRound,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { ConfirmModal, Alert, Button, EmptyState, TruncatedText, brandFillClass } from "@/components/ui";
import { ErrorState, LoadingState, SectionCard } from "@/components/common";
import { houseService } from "@/services/houseService";
import { deviceKeys } from "@/services/deviceService";
import { DeviceCredentialsModal } from "@/components/device/DeviceCredentialsModal";
import { ChangeRoomModal } from "@/components/device/ChangeRoomModal";
import { RoomChangeConfirmModal } from "@/components/device/RoomChangeConfirmModal";
import { ClickToCopyId } from "@/components/common";
import { DetailRow } from "@/components/ui";
import { toastApiError } from "@/lib/errorMessages";
import { statusDotClass } from "@/lib/statusDot";
import { presenceGuestDisplayName, formatPresenceCalendarDate } from "@/lib/presenceDisplay";
import type {
  Device,
  DeviceStructureLeaveImpact,
  HouseDevice,
  RoomPresenceAction,
} from "@/types";
import { DevicePresencePanel } from "./DevicePresencePanel";
import { SortableHouseDeviceRow } from "./SortableHouseDeviceRow";
import { useHouseDevicesReorder } from "./useHouseDevicesReorder";

function deviceUpcomingCount(device: Device): number {
  return Math.max(0, device.upcoming_presence_count ?? 0);
}

function HostDevicePresenceSummary({ device }: { device: Device }) {
  const { t } = useTranslation("houses");
  const upcoming = deviceUpcomingCount(device);
  const genericGuestName = t("detail.presences.genericGuestName");

  if (device.current_presence) {
    const guestName = presenceGuestDisplayName(
      device.current_presence.guest_name,
      genericGuestName,
    );
    const departureLabel = t("detail.guestDeparts", {
      date: formatPresenceCalendarDate(device.current_presence.departure_date),
    });
    return (
      <p className="text-sm text-success-700">
        <span className="font-medium">{guestName}</span>
        <span className="text-surface-500">
          {" · "}
          {departureLabel}
        </span>
        {upcoming > 0 ? (
          <span className="text-surface-500">
            {" · "}
            {t("detail.queuedGuests", { count: upcoming })}
          </span>
        ) : null}
      </p>
    );
  }

  if (upcoming > 0) {
    return (
      <p className="text-sm text-surface-600">
        {t("detail.noCurrentGuestWithQueue", { count: upcoming })}
      </p>
    );
  }

  return (
    <p className="text-sm text-surface-500">{t("detail.noGuestPresence")}</p>
  );
}

interface HouseDevicesSectionProps {
  houseId: string;
  isHost: boolean;
}

export function HouseDevicesSection({
  houseId,
  isHost,
}: HouseDevicesSectionProps) {
  const { t } = useTranslation("houses");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const { sensors, handleDragEnd, isReordering } =
    useHouseDevicesReorder(houseId);

  const [presencePanelKiosk, setPresencePanelKiosk] = useState<HouseDevice | null>(
    null,
  );
  const [credentialModalState, setCredentialModalState] = useState<{
    kioskCode: string;
    password: string;
    title: string;
    description: string;
  } | null>(null);
  const [regeneratingDeviceId, setRegeneratingDeviceId] = useState<
    string | null
  >(null);
  const [changeRoomDevice, setChangeRoomDevice] = useState<Device | null>(null);
  const [pendingRename, setPendingRename] = useState<{
    device: Device;
    nextName: string | null;
  } | null>(null);
  const [pendingRoomChange, setPendingRoomChange] = useState<{
    device: Device;
    nextName: string | null;
    impact: DeviceStructureLeaveImpact;
  } | null>(null);
  const [pendingRegenerate, setPendingRegenerate] = useState<Device | null>(
    null,
  );
  const [isSavingRename, setIsSavingRename] = useState(false);

  const {
    data: devices = [],
    refetch: refetchDevices,
    isFetching: isRefreshingDevices,
    isLoading: isLoadingDevices,
    error: devicesError,
  } = useQuery({
    queryKey: deviceKeys.byHouse(houseId),
    queryFn: () => houseService.listDevices(houseId),
    enabled: !!houseId,
  });
  // Loading / error replace the list (and its empty state) until data arrives.
  const devicesStatusView = isLoadingDevices ? (
    <LoadingState />
  ) : devicesError ? (
    <ErrorState
      message={t("loadDevices", { ns: "errors" })}
      onRetry={() => void refetchDevices()}
    />
  ) : null;
  // The count is only known once the list has loaded.
  const devicesTitle = devicesStatusView
    ? t("detail.tabs.devices")
    : t("detail.devicesTitle", { count: devices.length });

  const persistRename = async (
    device: Device,
    nextName: string | null,
    roomPresenceAction?: RoomPresenceAction,
  ) => {
    try {
      setIsSavingRename(true);
      const updatedDevice = await houseService.updateDevice(houseId, device.id, {
        room_name: nextName,
        room_presence_action: roomPresenceAction,
      });
      queryClient.setQueryData<HouseDevice[]>(
        deviceKeys.byHouse(houseId),
        (current = []) =>
          current.map((item) =>
            item.id === updatedDevice.id ? updatedDevice : item,
          ),
      );
      toast.success(t("deviceRenamed"));
      setChangeRoomDevice(null);
      setPendingRename(null);
      setPendingRoomChange(null);
    } catch (err) {
      toastApiError(err, "renameDevice");
    } finally {
      setIsSavingRename(false);
    }
  };

  const loadRoomChangeImpact = async (
    device: Device,
  ): Promise<DeviceStructureLeaveImpact> => {
    const [current, future] = await Promise.all([
      houseService.listDevicePresences(houseId, device.id, "current"),
      houseService.listDevicePresences(houseId, device.id, "future"),
    ]);
    return {
      current: current.map((p) => ({
        id: p.id,
        guest_name: p.guest_name?.trim() || t("detail.presences.genericGuestName"),
        nationality: p.nationality,
        arrival_date: p.arrival_date,
        arrival_time: p.arrival_time,
        departure_date: p.departure_date,
        departure_time: p.departure_time,
      })),
      future: future.map((p) => ({
        id: p.id,
        guest_name: p.guest_name?.trim() || t("detail.presences.genericGuestName"),
        nationality: p.nationality,
        arrival_date: p.arrival_date,
        arrival_time: p.arrival_time,
        departure_date: p.departure_date,
        departure_time: p.departure_time,
      })),
      has_impact: current.length > 0 || future.length > 0,
    };
  };

  const handleChangeRoomConfirm = async (nextName: string | null) => {
    if (isHost || !changeRoomDevice) return;
    const device = changeRoomDevice;
    const mayHaveOccupancy =
      device.current_presence != null || deviceUpcomingCount(device) > 0;
    if (mayHaveOccupancy) {
      try {
        setIsSavingRename(true);
        const impact = await loadRoomChangeImpact(device);
        if (impact.has_impact) {
          setPendingRoomChange({ device, nextName, impact });
          setChangeRoomDevice(null);
          return;
        }
      } catch (err) {
        toastApiError(err, "renameDevice");
        return;
      } finally {
        setIsSavingRename(false);
      }
    }
    if (device.house_id) {
      setPendingRename({ device, nextName });
      setChangeRoomDevice(null);
      return;
    }
    await persistRename(device, nextName);
  };

  const getDeviceStatus = (
    device: Device,
  ): { label: string; dotClass: string } => {
    if (device.is_revoked)
      return {
        label: t("detail.statusRevoked"),
        dotClass: statusDotClass(false),
      };
    if (!device.software_id)
      return { label: t("detail.statusCreated"), dotClass: "bg-surface-400" };
    return {
      label: t("detail.statusActive"),
      dotClass: statusDotClass(true),
    };
  };

  const handleRegenerateCredentials = (device: Device) => {
    setPendingRegenerate(device);
  };

  const confirmRegenerateCredentials = async () => {
    if (!pendingRegenerate) return;
    const device = pendingRegenerate;
    setRegeneratingDeviceId(device.id);
    try {
      const response = await houseService.resetDevicePassword(
        houseId,
        device.id,
      );
      setCredentialModalState({
        kioskCode: device.kiosk_code,
        password: response.password,
        title: t("detail.credentialModalTitle"),
        description: t("detail.credentialModalDesc"),
      });
      setPendingRegenerate(null);
      await refetchDevices();
      toast.success(t("credentialsRegenerated"));
    } catch (err) {
      toastApiError(err, "regenerateCredentials");
    } finally {
      setRegeneratingDeviceId(null);
    }
  };

  return (
    <div className="w-full min-w-0">
      {isHost ? (
        <div className="space-y-4">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="flex items-center gap-2 text-[15px] font-semibold tracking-tight text-surface-900">
                <Tablet className="h-4 w-4 text-surface-500" aria-hidden />
                {devicesTitle}
              </h3>
              {devices.length > 0 ? (
                <p className="mt-0.5 text-[13px] text-surface-500">
                  {t("detail.hostDevicesCaption")}
                </p>
              ) : null}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="shrink-0"
              onClick={() => void refetchDevices()}
              isLoading={isRefreshingDevices}
              leftIcon={<RefreshCcw className="h-4 w-4" />}
              aria-label={tCommon("refresh")}
            />
          </div>

          {devicesStatusView ?? (devices.length === 0 ? (
            <EmptyState
              variant="dashed"
              size="md"
              icon={Tablet}
              title={t("detail.noDevices")}
              description={t("detail.noDevicesHint")}
              className="rounded-xl"
            />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event: DragEndEvent) =>
                handleDragEnd(event, devices)
              }
            >
              <SortableContext
                items={devices.map((device) => device.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="divide-y divide-surface-100 overflow-hidden rounded-xl border border-surface-200 bg-surface-0 shadow-card">
                  {devices.map((device) => {
                    const hasPresence =
                      Boolean(device.current_presence) ||
                      deviceUpcomingCount(device) > 0;
                    return (
                    <SortableHouseDeviceRow
                      key={device.id}
                      id={device.id}
                      disabled={isReordering}
                      dragHandleLabel={t("detail.dragToReorder")}
                      className={
                        hasPresence
                          ? "flex flex-col gap-3 bg-success-50/40 px-4 py-3.5 transition-colors sm:flex-row sm:items-center sm:gap-4"
                          : "flex flex-col gap-3 px-4 py-3.5 transition-colors hover:bg-surface-50 sm:flex-row sm:items-center sm:gap-4 dark:hover:bg-surface-100/50"
                      }
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                        <div className="flex min-w-0 items-center gap-3">
                        <span
                          className={cn(
                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
                            hasPresence
                              ? "bg-success-50 text-success-600 ring-success-600/20"
                              : "bg-primary-50 text-primary-600 ring-primary-600/15",
                          )}
                          aria-hidden
                        >
                          <Tablet className="h-4 w-4" />
                        </span>
                        <TruncatedText
                          className={cn(
                            "min-w-0 text-sm font-medium",
                            device.room_name?.trim()
                              ? "text-surface-900"
                              : "text-surface-400",
                          )}
                        >
                          {device.room_name?.trim()
                            ? device.room_name
                            : t("changeRoomNoRoom")}
                        </TruncatedText>
                        </div>
                        <div className="min-w-0 pl-12">
                          <HostDevicePresenceSummary device={device} />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="inline-flex shrink-0 items-center gap-1.5 self-start text-sm font-medium text-primary-700 hover:text-primary-800 sm:self-auto"
                        onClick={() => setPresencePanelKiosk(device)}
                      >
                        <Users className="h-4 w-4" />
                        {hasPresence
                          ? t("detail.managePresences")
                          : t("detail.addPresences")}
                      </button>
                    </SortableHouseDeviceRow>
                    );
                  })}
                </ul>
              </SortableContext>
            </DndContext>
          ))}
        </div>
      ) : (
        <SectionCard
          title={devicesTitle}
          icon={Tablet}
          iconTone="plain"
          iconClassName="text-surface-600 shrink-0"
          titleClassName="text-base sm:text-lg"
          className="w-full"
          action={
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => void refetchDevices()}
              isLoading={isRefreshingDevices}
              leftIcon={<RefreshCcw className="h-4 w-4" />}
              aria-label={tCommon("refresh")}
            />
          }
        >
            {devicesStatusView ?? (devices.length === 0 ? (
              <EmptyState
                variant="dashed"
                size="md"
                icon={Tablet}
                title={t("detail.noDevices")}
                description={t("detail.noDevicesHint")}
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {devices.map((device) => {
                  const status = getDeviceStatus(device);
                  return (
                    <div
                      key={device.id}
                      className="flex animate-slide-up flex-col overflow-hidden rounded-xl border border-surface-200 bg-surface-0 shadow-card transition-all duration-200 hover:-translate-y-px hover:border-surface-300 hover:shadow-elevated"
                    >
                      <div className="border-b border-surface-200/70 bg-surface-50/70 px-4 py-3">
                        <div className="flex items-start gap-3">
                          <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-glow ring-1 ring-inset ring-white/15", brandFillClass("primary"))}>
                            <Tablet className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={`inline-flex h-2 w-2 shrink-0 rounded-full ${status.dotClass}`}
                                title={status.label}
                              />
                              <span className="text-xs font-medium text-surface-500">
                                {status.label}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center gap-1">
                              <TruncatedText
                                as="h3"
                                className={cn(
                                  "min-w-0 text-[15px] font-semibold tracking-tight",
                                  device.room_name?.trim()
                                    ? "text-surface-900"
                                    : "text-surface-400",
                                )}
                              >
                                {device.room_name?.trim() ||
                                  t("changeRoomNoRoom")}
                              </TruncatedText>
                              <button
                                type="button"
                                className="shrink-0 rounded-md p-1 text-surface-500 hover:bg-surface-100 hover:text-surface-700"
                                onClick={() => setChangeRoomDevice(device)}
                                title={
                                  device.room_name?.trim()
                                    ? t("changeRoomTitle")
                                    : t("addRoomTitle")
                                }
                                aria-label={
                                  device.room_name?.trim()
                                    ? t("changeRoomTitle")
                                    : t("addRoomTitle")
                                }
                              >
                                {device.room_name?.trim() ? (
                                  <ArrowLeftRight className="h-4 w-4" />
                                ) : (
                                  <SquarePlus className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-1 flex-col gap-3 p-4 text-sm text-surface-600">
                        <DetailRow label={t("detail.serialLabel")} layout="stacked">
                          <ClickToCopyId
                            value={device.serial_number}
                            className="w-full"
                            ellipsis="start"
                          />
                        </DetailRow>

                        {device.current_presence ? (
                          <Alert
                            variant="success"
                            className="rounded-lg px-3 py-2"
                            title={t("detail.currentGuest")}
                          >
                            {presenceGuestDisplayName(
                              device.current_presence.guest_name,
                              t("detail.presences.genericGuestName"),
                            )}
                            <span className="mt-1 block text-sm">
                              {t("detail.guestDeparts", {
                                date: formatPresenceCalendarDate(
                                  device.current_presence.departure_date,
                                ),
                              })}
                            </span>
                            {deviceUpcomingCount(device) > 0 ? (
                              <span className="mt-1 block text-sm">
                                {t("detail.queuedGuests", {
                                  count: deviceUpcomingCount(device),
                                })}
                              </span>
                            ) : null}
                          </Alert>
                        ) : deviceUpcomingCount(device) > 0 ? (
                          <Alert
                            variant="info"
                            className="rounded-lg px-3 py-2"
                            title={t("detail.noCurrentGuestWithQueue", {
                              count: deviceUpcomingCount(device),
                            })}
                          />
                        ) : (
                          <p className="rounded-lg border border-dashed border-surface-200 bg-surface-50 px-3 py-2 text-sm text-surface-500">
                            {t("detail.noGuestPresence")}
                          </p>
                        )}
                      </div>

                      <div className="mt-auto flex flex-wrap gap-2 border-t border-surface-100 bg-surface-50/50 px-4 py-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-w-[8rem] flex-1"
                          onClick={() => setPresencePanelKiosk(device)}
                        >
                          <Users className="mr-1 h-4 w-4" />
                          {t("detail.managePresences")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-w-[8rem] flex-1"
                          disabled={
                            device.is_revoked === true ||
                            regeneratingDeviceId === device.id
                          }
                          onClick={() => handleRegenerateCredentials(device)}
                        >
                          <KeyRound className="mr-1 h-4 w-4" />
                          {regeneratingDeviceId === device.id
                            ? t("detail.refreshingCredentials")
                            : t("detail.refreshCredentials")}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
        </SectionCard>
      )}

      <DeviceCredentialsModal
        isOpen={credentialModalState !== null}
        title={credentialModalState?.title ?? ""}
        description={credentialModalState?.description ?? ""}
        kioskCode={credentialModalState?.kioskCode ?? ""}
        password={credentialModalState?.password ?? ""}
        onClose={() => setCredentialModalState(null)}
      />

      {presencePanelKiosk && (
        <DevicePresencePanel
          isOpen={!!presencePanelKiosk}
          onClose={() => setPresencePanelKiosk(null)}
          houseId={houseId}
          kiosk={presencePanelKiosk}
        />
      )}

      {!isHost && changeRoomDevice ? (
        <ChangeRoomModal
          isOpen
          onClose={() => {
            if (!isSavingRename) setChangeRoomDevice(null);
          }}
          onConfirm={(nextName) => void handleChangeRoomConfirm(nextName)}
          title={t("changeRoomTitle")}
          currentRoomLabel={t("changeRoomCurrentLabel")}
          newRoomLabel={t("changeRoomNewLabel")}
          currentRoomName={changeRoomDevice.room_name}
          noRoomLabel={t("changeRoomNoRoom")}
          placeholder={t("changeRoomPlaceholder")}
          unchangedHint={t("changeRoomUnchanged")}
          confirmText={t("changeRoomConfirm")}
          removeText={t("changeRoomRemove")}
          cancelText={tCommon("cancel")}
          closeLabel={tCommon("close")}
          isLoading={isSavingRename}
        />
      ) : null}
      {!isHost ? (
        <ConfirmModal
          closeLabel={tCommon("close")}
          cancelText={tCommon("cancel")}
          confirmText={tCommon("confirm")}
          isOpen={pendingRename !== null}
          onClose={() => setPendingRename(null)}
          onConfirm={() => {
            if (!pendingRename) return;
            void persistRename(pendingRename.device, pendingRename.nextName);
          }}
          title={t("adminRenameConfirmTitle")}
          message={t("adminRenameConfirmMessage", {
            name:
              pendingRename?.nextName?.trim() || t("changeRoomNoRoom"),
          })}
          isLoading={isSavingRename}
        />
      ) : null}
      {!isHost && pendingRoomChange ? (
        <RoomChangeConfirmModal
          isOpen
          onClose={() => {
            if (!isSavingRename) setPendingRoomChange(null);
          }}
          onConfirm={(action) => {
            void persistRename(
              pendingRoomChange.device,
              pendingRoomChange.nextName,
              action,
            );
          }}
          title={t("roomChangeTitle")}
          intro={t("roomChangeIntro")}
          newRoomLabel={t("roomChangeNewLabel")}
          newRoomName={
            pendingRoomChange.nextName?.trim() || t("changeRoomNoRoom")
          }
          currentHeading={t("roomChangeCurrentHeading")}
          futureHeading={t("roomChangeFutureHeading")}
          transferHelp={t("roomChangeTransferHelp")}
          discardHelp={t("roomChangeDiscardHelp")}
          transferText={t("roomChangeTransfer")}
          discardText={t("roomChangeDiscard")}
          cancelText={tCommon("cancel")}
          closeLabel={tCommon("close")}
          impact={pendingRoomChange.impact}
          isConfirming={isSavingRename}
        />
      ) : null}
      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        confirmText={tCommon("confirm")}
        isOpen={pendingRegenerate != null}
        onClose={() => {
          if (!regeneratingDeviceId) setPendingRegenerate(null);
        }}
        onConfirm={() => void confirmRegenerateCredentials()}
        title={t("detail.refreshCredentials")}
        message={t("detail.regenerateConfirm", {
          name:
            pendingRegenerate?.room_name?.trim() || t("changeRoomNoRoom"),
        })}
        isLoading={regeneratingDeviceId != null}
      />
    </div>
  );
}
